import { Close, Help } from '@mui/icons-material'
import {
  Box,
  Button,
  Popover,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  styled,
  tableCellClasses,
} from '@mui/material'
import I18n from 'i18n-js'
import { useState } from 'react'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()((theme) => {
  return {
    permission: {
      fontSize: '11px',
    },
    description: {
      fontSize: '10px',
    },
    close: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
    },
  }
})

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`& .${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey['700'],
    color: theme.palette.common.white,
  },
}))

export function FacebookPermissionsExplanation() {
  const { classes } = useStyles()
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Button variant={'text'} color={'primary'} onClick={handleClick}>
        <Help fontSize={'small'} style={{ marginRight: '5px' }} />
        {I18n.t('facebookPermissionsExplanation.learnMore')}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box pt={4} pb={4} pl={4} pr={16}>
          <Typography variant={'h5'}>
            {I18n.t('facebookPermissionsExplanation.requestedPermissions')}
          </Typography>
          <Box pt={4}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissionsExplanation.permissions')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissionsExplanation.howItsUsed')}
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissions.pages_manage_posts.title')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.pages_manage_posts.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissions.pages_manage_ads.title')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.pages_manage_ads.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t(
                          'facebookPermissions.pages_manage_metadata.title',
                        )}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.pages_manage_metadata.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissions.read_insights.title')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.read_insights.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissions.ads_management.title')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.ads_management.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t(
                          'facebookPermissions.business_management.title',
                        )}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.business_management.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t('facebookPermissions.instagram_basic.title')}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.instagram_basic.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t(
                          'facebookPermissions.instagram_content_publish.title',
                        )}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.instagram_content_publish.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Typography variant={'subtitle1'}>
                        {I18n.t(
                          'facebookPermissions.instagram_manage_insights.title',
                        )}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant={'subtitle2'}>
                        {I18n.t(
                          'facebookPermissions.instagram_manage_insights.description',
                        )}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <IconButton
              className={classes.close}
              onClick={handleClose}
              size="large"
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </Popover>
    </>
  )
}
